import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// LLM Providers admin page (root only).
//
// Pick the active provider + model and manage per-provider API keys. Every
// change takes effect on the next LLM call — no backend restart. The page
// reads + writes the same app_settings KV store that the legacy env vars
// were derived from, so a saved choice survives a backend restart.
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ApiError, authApi, pricingApi, providersApi } from "@/lib/api";
export default function Providers() {
    const [me, setMe] = useState("loading");
    const [providers, setProviders] = useState([]);
    const [active, setActive] = useState({
        provider: "",
        model: "",
    });
    const [err, setErr] = useState(null);
    const [info, setInfo] = useState(null);
    // Form state for the "swap card"
    const [swapProvider, setSwapProvider] = useState("");
    const [swapModel, setSwapModel] = useState("");
    const [savingSwap, setSavingSwap] = useState(false);
    // Form state for the per-provider cards (key + base_url draft)
    const [drafts, setDrafts] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    const [testing, setTesting] = useState(null);
    const [testResults, setTestResults] = useState({});
    // Token pricing catalog + per-row drafts. Grouped by provider for display.
    const [prices, setPrices] = useState([]);
    const [draftPrices, setDraftPrices] = useState({});
    const [savingPrice, setSavingPrice] = useState(null);
    // Resolve current user (root only).
    useEffect(() => {
        authApi.me()
            .then((u) => setMe(u.role === "root" ? "ok" : "denied"))
            .catch(() => setMe("denied"));
    }, []);
    // Load the catalog once we're confirmed root.
    useEffect(() => {
        if (me !== "ok")
            return;
        void reload();
    }, [me]);
    const reload = async () => {
        try {
            const [data, priceData] = await Promise.all([
                providersApi.list(),
                pricingApi.list().catch(() => ({ models: [] })),
            ]);
            setProviders(data.providers);
            setActive(data.active);
            setPrices(priceData.models);
            // Seed the swap-card selects from the active config.
            setSwapProvider((cur) => cur || data.active.provider);
            setSwapModel((cur) => cur || data.active.model);
            // Seed per-provider drafts so the input fields have something to show.
            setDrafts((cur) => {
                const next = { ...cur };
                for (const p of data.providers) {
                    if (!next[p.id]) {
                        next[p.id] = { api_key: "", base_url: p.default_base_url ?? "", show_key: false };
                    }
                    else if (!next[p.id].base_url && p.default_base_url) {
                        next[p.id].base_url = p.default_base_url;
                    }
                }
                return next;
            });
            // Seed per-model price drafts from the catalog (so the inputs show
            // the current effective price, override or builtin).
            setDraftPrices((cur) => {
                const next = { ...cur };
                for (const m of priceData.models) {
                    if (!next[m.model]) {
                        next[m.model] = {
                            input: m.input,
                            output: m.output,
                            cache_write: m.cache_write,
                            cache_read: m.cache_read,
                        };
                    }
                }
                return next;
            });
            setErr(null);
        }
        catch (e) {
            setErr(e instanceof ApiError ? e.message : "failed to load providers");
        }
    };
    const setPriceDraft = (model, patch) => {
        setDraftPrices((cur) => ({ ...cur, [model]: { ...cur[model], ...patch } }));
    };
    const onSavePrice = async (m) => {
        const d = draftPrices[m.model];
        if (!d)
            return;
        setSavingPrice(m.model);
        setErr(null);
        setInfo(null);
        try {
            await pricingApi.set(m.model, {
                input: Number(d.input),
                output: Number(d.output),
                cache_write: Number(d.cache_write ?? 0),
                cache_read: Number(d.cache_read ?? 0),
            });
            setInfo(`Saved price for ${m.model}.`);
            await reload();
        }
        catch (e) {
            setErr(e instanceof ApiError ? e.message : `failed to save price for ${m.model}`);
        }
        finally {
            setSavingPrice(null);
        }
    };
    const onResetPrice = async (m) => {
        if (m.source !== "override")
            return;
        if (!confirm(`Reset ${m.model} to the built-in price?`))
            return;
        setSavingPrice(m.model);
        setErr(null);
        setInfo(null);
        try {
            await pricingApi.delete(m.model);
            setInfo(`Reset ${m.model} to built-in price.`);
            await reload();
        }
        catch (e) {
            setErr(e instanceof ApiError ? e.message : `failed to reset price for ${m.model}`);
        }
        finally {
            setSavingPrice(null);
        }
    };
    // Group models by provider_id so the pricing section reads top-down
    // (Anthropic → OpenAI → Google → …) like the catalog above.
    const groupedPrices = useMemo(() => {
        const groups = new Map();
        for (const p of prices) {
            const key = p.provider_id ?? "_other";
            if (!groups.has(key))
                groups.set(key, []);
            groups.get(key).push(p);
        }
        // Preserve catalog order: use the providers list as the canonical order,
        // then any leftover ("_other") at the end.
        const ordered = [];
        for (const p of providers) {
            const g = groups.get(p.id);
            if (g)
                ordered.push([p.id, g]);
        }
        if (groups.has("_other"))
            ordered.push(["_other", groups.get("_other")]);
        return ordered;
    }, [prices, providers]);
    if (me === "loading") {
        return (_jsx("div", { className: "mx-auto max-w-3xl px-4 py-12 text-sm text-muted", children: "Loading\u2026" }));
    }
    if (me === "denied")
        return _jsx(Navigate, { to: "/", replace: true });
    const activeProvider = providers.find((p) => p.id === active.provider);
    const setDraft = (id, patch) => {
        setDrafts((cur) => ({ ...cur, [id]: { ...cur[id], ...patch } }));
    };
    const onSwap = async () => {
        if (!swapProvider || !swapModel.trim())
            return;
        setSavingSwap(true);
        setErr(null);
        setInfo(null);
        try {
            const next = await providersApi.setActive(swapProvider, swapModel.trim());
            setActive(next);
            setInfo(`Active provider set to ${swapProvider} / ${swapModel.trim()}.`);
            // Reload so is_active badges and key indicators refresh everywhere.
            await reload();
        }
        catch (e) {
            setErr(e instanceof ApiError ? e.message : "failed to swap provider");
        }
        finally {
            setSavingSwap(false);
        }
    };
    const onSaveKey = async (p) => {
        const draft = drafts[p.id];
        if (!draft || !draft.api_key.trim()) {
            setErr("API key is required to save.");
            return;
        }
        setSavingKey(p.id);
        setErr(null);
        setInfo(null);
        try {
            await providersApi.setKey(p.id, draft.api_key.trim(), draft.base_url || null);
            setInfo(`Saved key for ${p.name}.`);
            setDraft(p.id, { api_key: "" }); // clear the input after save
            await reload();
        }
        catch (e) {
            setErr(e instanceof ApiError ? e.message : `failed to save key for ${p.name}`);
        }
        finally {
            setSavingKey(null);
        }
    };
    const onClearKey = async (p) => {
        if (!confirm(`Clear the saved API key for ${p.name}? The provider will fall back to env vars (if set).`))
            return;
        setSavingKey(p.id);
        setErr(null);
        setInfo(null);
        try {
            await providersApi.deleteKey(p.id);
            setInfo(`Cleared key for ${p.name}.`);
            await reload();
        }
        catch (e) {
            setErr(e instanceof ApiError ? e.message : `failed to clear key for ${p.name}`);
        }
        finally {
            setSavingKey(null);
        }
    };
    const onTest = async (p, model) => {
        setTesting(p.id);
        setTestResults((cur) => {
            const next = { ...cur };
            delete next[p.id];
            return next;
        });
        try {
            const m = (model ?? active.model) || p.default_model;
            const result = await providersApi.test(p.id, m);
            setTestResults((cur) => ({ ...cur, [p.id]: result }));
        }
        catch (e) {
            setTestResults((cur) => ({
                ...cur,
                [p.id]: { ok: false, error: e instanceof ApiError ? e.message : "test failed" },
            }));
        }
        finally {
            setTesting(null);
        }
    };
    return (_jsxs("div", { className: "mx-auto max-w-3xl space-y-6 px-4 pb-12 pt-6 sm:px-6 sm:pt-8", children: [_jsx("div", { className: "flex items-end justify-between gap-4", children: _jsxs("div", { children: [_jsx(Link, { to: "/", className: "text-xs text-muted transition-colors hover:text-accent", children: "\u2190 Back to workspace" }), _jsx("h1", { className: "mt-2 font-serif text-3xl font-semibold leading-tight tracking-tight sm:text-4xl", children: "LLM Providers" }), _jsx("p", { className: "mt-1.5 text-sm text-muted", children: "Pick the active provider and model, and store API keys. Changes take effect on the next LLM call \u2014 no backend restart needed. Already in-flight streams keep using their current chat client." })] }) }), err && (_jsx("div", { className: "rounded border border-danger/30 bg-danger/10 px-3 py-2 text-tx-xs text-danger", children: err })), info && (_jsx("div", { className: "rounded border border-success/30 bg-success/10 px-3 py-2 text-tx-xs text-success", children: info })), _jsxs("section", { className: "rounded-2xl border border-border bg-surface p-4 sm:p-5", children: [_jsx("h2", { className: "text-sm font-semibold uppercase tracking-[0.16em] text-subtle", children: "Active provider" }), _jsxs("p", { className: "mt-1 text-tx-xs text-muted", children: ["Currently routing LLM calls to:", " ", _jsx("span", { className: "font-mono text-text", children: activeProvider ? activeProvider.name : active.provider }), " ", _jsx("span", { className: "text-muted", children: "/" }), " ", _jsx("span", { className: "font-mono text-text", children: active.model })] }), _jsxs("div", { className: "mt-4 grid gap-3 sm:grid-cols-[1fr_1.5fr_auto]", children: [_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block text-tx-xs font-medium text-muted", children: "Provider" }), _jsx("select", { className: "field min-h-touch w-full text-sm", value: swapProvider, onChange: (e) => {
                                            const id = e.target.value;
                                            setSwapProvider(id);
                                            const def = providers.find((p) => p.id === id);
                                            if (def)
                                                setSwapModel(def.default_model);
                                        }, children: providers.map((p) => (_jsx("option", { value: p.id, children: p.name }, p.id))) })] }), _jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block text-tx-xs font-medium text-muted", children: "Model" }), _jsx("input", { type: "text", className: "field min-h-touch w-full text-sm", value: swapModel, onChange: (e) => setSwapModel(e.target.value), list: "provider-default-models", placeholder: providers.find((p) => p.id === swapProvider)?.default_model ?? "" }), _jsx("datalist", { id: "provider-default-models", children: (providers.find((p) => p.id === swapProvider)?.default_models ?? []).map((m) => (_jsx("option", { value: m }, m))) })] }), _jsx("div", { className: "flex items-end", children: _jsx("button", { type: "button", onClick: onSwap, disabled: savingSwap ||
                                        !swapProvider ||
                                        !swapModel.trim() ||
                                        (swapProvider === active.provider && swapModel.trim() === active.model), className: "min-h-touch w-full rounded-md border border-accent bg-accent/15 px-4 py-2 text-sm font-medium text-accent transition-colors hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto", children: savingSwap ? "Saving…" : "Set as active" }) })] })] }), _jsxs("section", { className: "space-y-3", children: [_jsx("h2", { className: "text-sm font-semibold uppercase tracking-[0.16em] text-subtle", children: "API keys" }), _jsxs("p", { className: "text-tx-xs text-muted", children: ["Stored in the server's ", _jsx("span", { className: "font-mono", children: "app_settings" }), " ", "table. Values are kept verbatim (not encrypted at rest) \u2014 matching the security posture of ", _jsx("span", { className: "font-mono", children: ".env" }), " keys."] }), providers.map((p) => {
                        const draft = drafts[p.id] ?? {
                            api_key: "",
                            base_url: p.default_base_url ?? "",
                            show_key: false,
                        };
                        const isActive = p.id === active.provider;
                        const testResult = testResults[p.id];
                        const saving = savingKey === p.id;
                        const isTesting = testing === p.id;
                        return (_jsxs("div", { className: `rounded-2xl border bg-surface p-4 sm:p-5 ${isActive ? "border-accent/50" : "border-border"}`, children: [_jsx("div", { className: "flex items-start justify-between gap-3", children: _jsxs("div", { className: "min-w-0", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("h3", { className: "text-sm font-semibold text-text", children: p.name }), isActive && (_jsx("span", { className: "rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-tx-xs font-medium text-accent", children: "Active" })), _jsx(KeyStatePill, { hasKey: p.has_key })] }), _jsxs("p", { className: "mt-1 text-tx-xs text-muted", children: ["Default base URL:", " ", _jsx("span", { className: "font-mono text-text", children: p.default_base_url ?? "(provider default)" })] })] }) }), _jsxs("div", { className: "mt-3 grid gap-3", children: [_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block text-tx-xs font-medium text-muted", children: "API key" }), _jsxs("div", { className: "relative", children: [_jsx("input", { type: draft.show_key ? "text" : "password", autoComplete: "off", spellCheck: false, className: "field min-h-touch w-full pr-20 font-mono text-sm", value: draft.api_key, onChange: (e) => setDraft(p.id, { api_key: e.target.value }), placeholder: p.has_key
                                                                ? "••••••••• (a key is already configured — type to replace)"
                                                                : "Paste your API key" }), _jsx("button", { type: "button", onClick: () => setDraft(p.id, { show_key: !draft.show_key }), className: "absolute inset-y-0 right-2 my-1 rounded border border-border bg-bg px-2 text-tx-xs text-muted hover:text-text", children: draft.show_key ? "Hide" : "Show" })] })] }), _jsxs("label", { className: "block", children: [_jsxs("span", { className: "mb-1 block text-tx-xs font-medium text-muted", children: ["Base URL", " ", _jsx("span", { className: "font-normal text-muted", children: "(optional \u2014 leave blank to use the provider default)" })] }), _jsx("input", { type: "text", autoComplete: "off", spellCheck: false, className: "field min-h-touch w-full font-mono text-sm", value: draft.base_url, onChange: (e) => setDraft(p.id, { base_url: e.target.value }), placeholder: p.default_base_url ?? "" })] })] }), _jsxs("div", { className: "mt-3 flex flex-wrap items-center gap-2", children: [_jsx("button", { type: "button", onClick: () => onSaveKey(p), disabled: saving || !draft.api_key.trim(), className: "min-h-touch rounded-md border border-accent bg-accent/15 px-3 py-1.5 text-tx-xs font-medium text-accent transition-colors hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-50", children: saving ? "Saving…" : "Save key" }), p.has_key && (_jsx("button", { type: "button", onClick: () => onClearKey(p), disabled: saving, className: "min-h-touch rounded-md border border-border bg-bg px-3 py-1.5 text-tx-xs text-muted transition-colors hover:border-danger/40 hover:text-danger disabled:cursor-not-allowed disabled:opacity-50", children: "Clear" })), _jsx("button", { type: "button", onClick: () => onTest(p), disabled: isTesting || !p.has_key, title: p.has_key
                                                ? `Send a 1-token ping to ${p.name}`
                                                : `Save an API key first to test ${p.name}`, className: "min-h-touch rounded-md border border-border bg-bg px-3 py-1.5 text-tx-xs text-muted transition-colors hover:border-accent/40 hover:text-accent disabled:cursor-not-allowed disabled:opacity-50", children: isTesting ? "Testing…" : "Test connection" }), testResult && (_jsx("span", { className: `text-tx-xs ${testResult.ok ? "text-success" : "text-danger"}`, children: testResult.ok
                                                ? `✓ ${testResult.echo?.slice(0, 60) || "OK"}`
                                                : `✗ ${testResult.error}` }))] })] }, p.id));
                    })] }), _jsxs("section", { className: "space-y-3", children: [_jsx("h2", { className: "text-sm font-semibold uppercase tracking-[0.16em] text-subtle", children: "Token pricing" }), _jsxs("p", { className: "text-tx-xs text-muted", children: ["Per-million-token USD prices used for cost accounting. Built-in defaults come from ", _jsx("span", { className: "font-mono", children: "memory/token_counter.py" }), "; an admin override wins without a backend restart. Set your own numbers if you have negotiated rates or are running a private deployment."] }), groupedPrices.length === 0 && (_jsx("div", { className: "rounded-2xl border border-border bg-surface p-4 text-tx-xs text-muted", children: "No pricing entries yet." })), _jsx("div", { className: "overflow-hidden rounded-2xl border border-border bg-surface", children: _jsxs("table", { className: "w-full text-sm", children: [_jsx("thead", { children: _jsxs("tr", { className: "border-b border-border bg-elevated/40 text-left text-tx-xs uppercase tracking-wider text-muted", children: [_jsx("th", { className: "px-3 py-2 font-medium", children: "Model" }), _jsx("th", { className: "px-2 py-2 text-right font-medium", children: "Input" }), _jsx("th", { className: "px-2 py-2 text-right font-medium", children: "Output" }), _jsx("th", { className: "px-2 py-2 text-right font-medium", children: "Cache write" }), _jsx("th", { className: "px-2 py-2 text-right font-medium", children: "Cache read" }), _jsx("th", { className: "px-3 py-2 font-medium", children: "Source" }), _jsx("th", { className: "px-3 py-2 text-right font-medium", children: "Actions" })] }) }), _jsx("tbody", { children: groupedPrices.map(([providerId, rows]) => {
                                        const providerName = providers.find((p) => p.id === providerId)?.name ?? "(other)";
                                        return (_jsx(ProviderPriceGroup, { providerName: providerName, rows: rows, draftPrices: draftPrices, savingPrice: savingPrice, setPriceDraft: setPriceDraft, onSavePrice: onSavePrice, onResetPrice: onResetPrice }, providerId));
                                    }) })] }) })] })] }));
}
function ProviderPriceGroup({ providerName, rows, draftPrices, savingPrice, setPriceDraft, onSavePrice, onResetPrice, }) {
    return (_jsxs(_Fragment, { children: [_jsx("tr", { className: "bg-elevated/20", children: _jsx("td", { colSpan: 7, className: "px-3 py-1.5 text-tx-xs font-semibold uppercase tracking-wider text-muted", children: providerName }) }), rows.map((m) => {
                const d = draftPrices[m.model] ?? {
                    input: m.input,
                    output: m.output,
                    cache_write: m.cache_write,
                    cache_read: m.cache_read,
                };
                const saving = savingPrice === m.model;
                const dirty = Number(d.input) !== m.input ||
                    Number(d.output) !== m.output ||
                    Number(d.cache_write ?? 0) !== m.cache_write ||
                    Number(d.cache_read ?? 0) !== m.cache_read;
                return (_jsxs("tr", { className: "border-t border-border/60", children: [_jsx("td", { className: "px-3 py-2 font-mono text-tx-xs text-text", children: m.model }), _jsx("td", { className: "px-2 py-2", children: _jsx(PriceInput, { value: d.input, onChange: (v) => setPriceDraft(m.model, { input: v }) }) }), _jsx("td", { className: "px-2 py-2", children: _jsx(PriceInput, { value: d.output, onChange: (v) => setPriceDraft(m.model, { output: v }) }) }), _jsx("td", { className: "px-2 py-2", children: _jsx(PriceInput, { value: d.cache_write ?? 0, onChange: (v) => setPriceDraft(m.model, { cache_write: v }) }) }), _jsx("td", { className: "px-2 py-2", children: _jsx(PriceInput, { value: d.cache_read ?? 0, onChange: (v) => setPriceDraft(m.model, { cache_read: v }) }) }), _jsx("td", { className: "px-3 py-2", children: m.source === "override" ? (_jsx("span", { className: "rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-tx-xs font-medium text-accent", children: "Override" })) : (_jsx("span", { className: "rounded-full border border-border bg-bg px-2 py-0.5 text-tx-xs font-medium text-muted", children: "Built-in" })) }), _jsx("td", { className: "px-3 py-2 text-right", children: _jsxs("div", { className: "flex items-center justify-end gap-2", children: [_jsx("button", { type: "button", onClick: () => onSavePrice(m), disabled: saving || !dirty, className: "min-h-touch rounded-md border border-accent bg-accent/15 px-2.5 py-1 text-tx-xs font-medium text-accent transition-colors hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-50", children: saving ? "…" : "Save" }), m.source === "override" && (_jsx("button", { type: "button", onClick: () => onResetPrice(m), disabled: saving, className: "min-h-touch rounded-md border border-border bg-bg px-2.5 py-1 text-tx-xs text-muted transition-colors hover:border-danger/40 hover:text-danger disabled:cursor-not-allowed disabled:opacity-50", children: "Reset" }))] }) })] }, m.model));
            })] }));
}
function PriceInput({ value, onChange, }) {
    return (_jsx("input", { type: "number", step: "0.01", min: 0, inputMode: "decimal", className: "field min-h-touch w-20 text-right font-mono text-tx-xs", value: Number.isFinite(value) ? value : 0, onChange: (e) => {
            const v = parseFloat(e.target.value);
            onChange(Number.isFinite(v) ? v : 0);
        } }));
}
function KeyStatePill({ hasKey }) {
    if (hasKey) {
        return (_jsx("span", { className: "rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-tx-xs font-medium text-success", children: "Key set" }));
    }
    return (_jsx("span", { className: "rounded-full border border-warning/30 bg-warning/10 px-2 py-0.5 text-tx-xs font-medium text-warning", children: "No key" }));
}
